import Vue from 'vue'
import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'
import moment from 'moment-timezone'

const defaultAvailabilityRulesState = {
  filters: {
    appointmentTypeIds: [],
    endDate: null,
    hideExpiredRules: true,
    startDate: null,
  },

  pagination: {
    itemsPerPage: 20,
    page: 1,
    pageCount: 0,
    pageStart: 0,
    pageStop: 0,
    totalItems: 0,
  },

  pendingRequestCount: 0,
  rules: [],

  sort: {
    by: 'start_date',
    desc: false,
  },
}

export const AvailabilityRulesState = {
  availabilityRules: { ...defaultAvailabilityRulesState },
}

function mapRuleForApi (rule, userId, timezone) {
  return {
    availability_rule: {
      docket_entry_type_id: rule.docketEntryTypeId,
      end_date: moment(rule.endDate).format(),
      is_recurring: rule.isRecurring,
      availability_rule_slots_attributes: rule.slots.map(s => {
        return {
          _destroy: !!s._destroy,
          end_time: moment.tz(`2000-01-01 ${s.endTime}`, timezone).format(),
          id: s.id,
          start_time: moment.tz(`2000-01-01 ${s.startTime}`, timezone).format(),
          weekday: s.weekday,
        }
      }),
      start_date: moment(rule.startDate).format(),
      user_id: userId,
    },
  }
}

function mapRuleFromApi (rule, timezone) {
  return {
    docketEntryTypeId: rule.docket_entry_type_id,
    endDate: moment(rule.end_date).tz(timezone).format(),
    exceptions: rule.availability_exceptions.map(e => {
      return {
        endDate: moment(e.end_date).tz(timezone).format(),
        id: e.id,
        startDate: moment(e.start_date).tz(timezone).format(),
      }
    }),
    id: rule.id,
    isRecurring: rule.is_recurring,
    slots: rule.availability_rule_slots.map(s => {
      return {
        availabilityRuleId: s.availability_rule_id,
        endTime: moment(s.end_time).tz(timezone).format('HH:mm'),
        id: s.id,
        startTime: moment(s.start_time).tz(timezone).format('HH:mm'),
        weekday: s.weekday,
      }
    }),
    startDate: moment.tz(rule.start_date, timezone).format(),
  }
}

function mapRulesFromApi (rules, timezone) {
  return rules.map(r => mapRuleFromApi(r, timezone))
}

export const AvailabilityRulesActions = {
  async createAvailabilityRule ({ commit, dispatch, state }, rule) {
    return axiosRequest.post(
      '/calendar/availability_rules',
      mapRuleForApi(rule, state.selectedCalendar.userId, state.selectedCalendar.timezone)
    ).
      then(_ => dispatch('refreshAvailabilityRules')).
      catch(handleError)
  },

  async createAvailabilityException ({ commit, state }, { ruleId, startDate, endDate }) {
    return axiosRequest.post('/calendar/availability_exceptions', {
      availability_exception: {
        availability_rule_id: ruleId,
        end_date: endDate,
        start_date: startDate,
      },
    }).
      then(({ data }) => {
        commit('updateAvailabilityRule', mapRuleFromApi(data.availability_rule, state.selectedCalendar.timezone))
      }).
      catch(handleError)
  },

  async deleteAvailabilityRule ({ dispatch }, ruleId) {
    return axiosRequest.delete(`/calendar/availability_rules/${ruleId}`).
      then(_ => dispatch('refreshAvailabilityRules')).
      catch(handleError)
  },

  async deleteAvailabilityException ({ commit, state }, exceptionId) {
    return axiosRequest.delete(`calendar/availability_exceptions/${exceptionId}`).
      then(({ data }) => {
        commit('updateAvailabilityRule', mapRuleFromApi(data.availability_rule, state.selectedCalendar.timezone))
      }).
      catch(handleError)
  },

  async refreshAvailabilityRules ({ commit, state }) {
    commit('addAvailabilityRulesPendingRequest')

    const appointmentTypes = state.availabilityRules.filters.appointmentTypeIds || []
    if (appointmentTypes.length === 0) appointmentTypes.push(0)

    return axiosRequest.get('/calendar/availability_rules', {
      params: {
        appointment_types: appointmentTypes,
        end_date: state.availabilityRules.filters.endDate,
        hide_expired_rules: state.availabilityRules.filters.hideExpiredRules,
        items_per_page: state.availabilityRules.pagination.itemsPerPage,
        page: state.availabilityRules.pagination.page,
        start_date: state.availabilityRules.filters.startDate,
        sort_by: state.availabilityRules.sort.by,
        sort_order: state.availabilityRules.sort.desc ? 'DESC' : 'ASC',
        user_id: state.selectedCalendar.userId,
      },
    }).
      then(({ data }) => {
        commit('setAvailabilityRulesPagination', {
          itemsPerPage: data.items_per_page,
          page: data.page,
          pageCount: data.page_count,
          pageStart: data.page_start,
          pageStop: data.page_stop,
          totalItems: data.total_items,
        })

        commit('setAvailabilityRulesFilters', {
          appointmentTypeIds: data.appointment_types,
          endDate: data.end_date,
          hideExpiredRules: data.hide_expired_rules,
          startDate: data.start_date,
        })

        commit('updateAvailabilityRules', mapRulesFromApi(data.items, state.selectedCalendar.timezone))
      }).
      catch(handleError).
      finally(_ => {
        commit('removeAvailabilityRulesPendingRequest')
      })
  },

  async resetAvailabilityRules ({ commit }) {
    commit('resetAvailabilityRules')
  },

  async setAvailabilityRulesFilters ({ commit }, options) {
    commit('setAvailabilityRulesFilters', options)
  },

  async setAvailabilityRulesPagination ({ commit }, options) {
    commit('setAvailabilityRulesPagination', options)
  },

  async updateAvailabilityRule ({ commit, state }, rule) {
    return axiosRequest.put(
      `/calendar/availability_rules/${rule.id}`,
      mapRuleForApi(rule, rule.userId, state.selectedCalendar.timezone)
    ).
      then(({ data }) => {
        commit('updateAvailabilityRule', mapRuleFromApi(data.availability_rule, state.selectedCalendar.timezone))
      }).
      catch(handleError)
  },
}

export const AvailabilityRulesMutations = {
  addAvailabilityRulesPendingRequest: state => {
    state.availabilityRules.pendingRequestCount++
  },

  updateAvailabilityRule: (state, rule) => {
    const index = state.availabilityRules.rules.findIndex(ar => ar.id === rule.id)
    if (index < 0) return
    Vue.set(state.availabilityRules.rules, index, rule)
  },

  updateAvailabilityRules: (state, rules) => {
    state.availabilityRules.rules = rules
  },

  removeAvailabilityRulesPendingRequest: state => {
    state.availabilityRules.pendingRequestCount--
  },

  resetAvailabilityRules: state => {
    state.availabilityRules = { ...defaultAvailabilityRulesState }
  },

  setAvailabilityRulesFilters: (state, options) => {
    state.availabilityRules.filters = {
      ...state.availabilityRules.filters,
      ...options,
    }
  },

  setAvailabilityRulesPagination: (state, options) => {
    state.availabilityRules.pagination = {
      ...state.availabilityRules.pagination,
      ...options,
    }
  },
}

export const AvailabilityRulesGetters = {
  availabilityRules: state => state.availabilityRules.rules,
  availabilityRulesFilters: state => state.availabilityRules.filters,
  availabilityRulesHavePendingRequests: state => state.availabilityRules.pendingRequestCount !== 0,
  availabilityRulesPagination: state => state.availabilityRules.pagination,
  availabilityRulesPendingRequestCount: state => state.availabilityRules.pendingRequestCount,
}
